import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Alert,
  Spinner,
  Badge,
  Button,
  Row,
  Col,
  Table
} from 'reactstrap';
import instance from '../../api/Axios';

const BeneficiaryDetail = () => {
  const { beneficiaryId } = useParams();
  const navigate = useNavigate();
  const [beneficiary, setBeneficiary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBeneficiaryDetail = async () => {
      try {
        setLoading(true);
        setError('');
        console.log('=== FETCHING BENEFICIARY DETAIL ===');
        console.log('Beneficiary ID:', beneficiaryId);
        
        const response = await instance.get(`/api/accounts/beneficiaries/${beneficiaryId}/`);
        console.log('Beneficiary detail response:', response.data);
        setBeneficiary(response.data);
      } catch (error) {
        console.error('Error fetching beneficiary detail:', error);
        if (error.response?.status === 404) {
          setError('Beneficiary not found.');
        } else if (error.response?.status === 403) {
          setError('You do not have permission to view this beneficiary.');
        } else {
          setError('Failed to fetch beneficiary details. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (beneficiaryId) {
      fetchBeneficiaryDetail();
    }
  }, [beneficiaryId]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return '-';
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'ZAR',
    });
    return formatter.format(amount);
  };

  const formatIdDocumentType = (val) => {
    if (!val) return '-';
    const map = {
      pending: '-',
      passport: 'Passport',
      national_id: 'National ID',
      drivers_license: 'Drivers License',
      refugee_travel_document: 'Refugee Travel Document',
    };
    return map[val] || val;
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'info';
      case 'active':
        return 'success';
      case 'suspended':
        return 'warning';
      case 'closed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const handleGoBack = () => {
    // Go back to the bursary detail page
    if (beneficiary?.bursary_info?.id) {
      navigate(`/general-dashboard/bursaries/${beneficiary.bursary_info.id}`);
    } else {
      navigate('/general-dashboard/bursaries');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner color="primary" />
        <span className="ms-2">Loading beneficiary details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Button 
            color="secondary" 
            onClick={handleGoBack}
            className="d-flex align-items-center"
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Bursary
          </Button>
        </div>
        <Alert color="danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      </div>
    );
  }

  if (!beneficiary) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Button 
            color="secondary" 
            onClick={handleGoBack}
            className="d-flex align-items-center"
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Bursary
          </Button>
        </div>
        <Alert color="warning">
          <i className="fas fa-info-circle me-2"></i>
          Beneficiary not found.
        </Alert>
      </div>
    );
  }

  return (
    <div>
      {/* Header with navigation buttons */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button 
          color="secondary" 
          onClick={handleGoBack}
          className="d-flex align-items-center"
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Bursary
        </Button>
      </div>

      {/* Main content card */}
      <Card>
        <CardHeader className="bg-primary text-white">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i className="fas fa-user-graduate me-3 fa-2x"></i>
              <div>
                <h4 className="mb-0">Beneficiary Details</h4>
                <small>{beneficiary.first_name_other} {beneficiary.surname}</small>
              </div>
            </div>
            <Badge 
              color={getStatusBadgeColor(beneficiary.student_status)} 
              className="fs-6 px-3 py-2"
            >
              {beneficiary.student_status?.toUpperCase() || 'UNKNOWN'}
            </Badge>
          </div>
        </CardHeader>
        
        <CardBody>
          <Row>
            {/* Personal Information */}
            <Col md={6}>
              <Card className="mb-4">
                <CardHeader className="bg-light">
                  <h5 className="mb-0">
                    <i className="fas fa-user me-2"></i>
                    Personal Information
                  </h5>
                </CardHeader>
                <CardBody>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Title:</strong></td>
                        <td>{beneficiary.title || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Surname:</strong></td>
                        <td>{beneficiary.surname}</td>
                      </tr>
                      <tr>
                        <td><strong>First Name & Other Names:</strong></td>
                        <td>{beneficiary.first_name_other}</td>
                      </tr>
                      <tr>
                        <td><strong>ID Document Type:</strong></td>
                        <td>{formatIdDocumentType(beneficiary.id_document_type)}</td>
                      </tr>
                      <tr>
                        <td><strong>National ID:</strong></td>
                        <td>
                          <span className="font-monospace">
                            {beneficiary.national_id || '-'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Gender:</strong></td>
                        <td>{beneficiary.gender || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Race:</strong></td>
                        <td>{beneficiary.race || '-'}</td>
                      </tr>
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>

            {/* Contact Information */}
            <Col md={6}>
              <Card className="mb-4">
                <CardHeader className="bg-light">
                  <h5 className="mb-0">
                    <i className="fas fa-phone me-2"></i>
                    Contact Information
                  </h5>
                </CardHeader>
                <CardBody>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Email Address:</strong></td>
                        <td>
                          <a href={`mailto:${beneficiary.email_address}`}>
                            {beneficiary.email_address}
                          </a>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Mobile Number:</strong></td>
                        <td>
                          <a href={`tel:${beneficiary.mobile_number}`}>
                            {beneficiary.mobile_number}
                          </a>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>

            {/* Academic Information */}
            <Col md={6}>
              <Card className="mb-4">
                <CardHeader className="bg-light">
                  <h5 className="mb-0">
                    <i className="fas fa-graduation-cap me-2"></i>
                    Academic Information
                  </h5>
                </CardHeader>
                <CardBody>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Institution:</strong></td>
                        <td>{beneficiary.institution_name || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Campus:</strong></td>
                        <td>{beneficiary.campus_name || beneficiary.campus || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Student Number:</strong></td>
                        <td>
                          <span className="font-monospace">
                            {beneficiary.student_number || '-'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Field of Study:</strong></td>
                        <td>{beneficiary.field_of_study || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Faculty:</strong></td>
                        <td>{beneficiary.faculty || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Year of Study:</strong></td>
                        <td>{beneficiary.year_of_study || '-'}</td>
                      </tr>
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>

            {/* Bursary Information */}
            <Col md={6}>
              <Card className="mb-4">
                <CardHeader className="bg-light">
                  <h5 className="mb-0">
                    <i className="fas fa-money-bill-wave me-2"></i>
                    Bursary Information
                  </h5>
                </CardHeader>
                <CardBody>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Entry Date:</strong></td>
                        <td>{formatDate(beneficiary.entry_date)}</td>
                      </tr>
                      <tr>
                        <td><strong>Exit Date:</strong></td>
                        <td>{formatDate(beneficiary.exit_date)}</td>
                      </tr>
                      <tr>
                        <td><strong>Status:</strong></td>
                        <td>
                          <Badge color={getStatusBadgeColor(beneficiary.student_status)}>
                            {beneficiary.student_status?.toUpperCase() || 'UNKNOWN'}
                          </Badge>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Bursary Program Details */}
          {beneficiary.bursary_info && (
            <Card className="mb-4">
              <CardHeader className="bg-info text-white">
                <h5 className="mb-0">
                  <i className="fas fa-award me-2"></i>
                  Bursary Program Details
                </h5>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Bursary Name:</strong></td>
                          <td>{beneficiary.bursary_info.name}</td>
                        </tr>
                        <tr>
                          <td><strong>Bursary Code:</strong></td>
                          <td>
                            <span className="font-monospace">
                              {beneficiary.bursary_info.bursary_code || '-'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Program Status:</strong></td>
                          <td>
                            <Badge color={getStatusBadgeColor(beneficiary.bursary_info.status)}>
                              {beneficiary.bursary_info.status?.toUpperCase() || 'UNKNOWN'}
                            </Badge>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={6}>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Start Date:</strong></td>
                          <td>{formatDate(beneficiary.bursary_info.start_date)}</td>
                        </tr>
                        <tr>
                          <td><strong>End Date:</strong></td>
                          <td>{formatDate(beneficiary.bursary_info.end_date)}</td>
                        </tr>
                        <tr>
                          <td><strong>Total Funding:</strong></td>
                          <td>
                            <strong className="text-success">
                              {formatCurrency(
                                beneficiary.bursary_info.funding_amount, 
                                beneficiary.bursary_info.bursary_currency
                              )}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          {/* Beneficiary Bursary Assignment Information */}
          {beneficiary.beneficiary_bursary_info && (
            <Card className="mb-4">
              <CardHeader className="bg-success text-white">
                <h5 className="mb-0">
                  <i className="fas fa-id-badge me-2"></i>
                  Beneficiary Bursary Assignment
                </h5>
              </CardHeader>
              <CardBody>
                {beneficiary.beneficiary_bursary_info.is_bursary_assigned ? (
                  <Row>
                    <Col md={6}>
                      <Table borderless size="sm">
                        <tbody>
                          <tr>
                            <td><strong>Beneficiary Bursary Code:</strong></td>
                            <td>
                              <span className="font-monospace text-primary fw-bold">
                                {beneficiary.beneficiary_bursary_info.beneficiary_bursary_code || 'Not Generated'}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Assignment Status:</strong></td>
                            <td>
                              <Badge color={beneficiary.beneficiary_bursary_info.bursary_matches ? 'success' : 'warning'}>
                                {beneficiary.beneficiary_bursary_info.bursary_matches ? 'CORRECTLY ASSIGNED' : 'ASSIGNMENT ERROR'}
                              </Badge>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6}>
                      <Table borderless size="sm">
                        <tbody>
                          <tr>
                            <td><strong>Date Assigned:</strong></td>
                            <td>{formatDate(beneficiary.beneficiary_bursary_info.bursary_assigned_date)}</td>
                          </tr>
                          <tr>
                            <td><strong>Beneficiary ID:</strong></td>
                            <td>
                              <span className="font-monospace">
                                {beneficiary.beneficiary_bursary_info.beneficiary_id || '-'}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-exclamation-circle fa-3x text-warning mb-3"></i>
                    <h6 className="text-muted">No Beneficiary Bursary Assignment</h6>
                    <p className="text-muted mb-0">
                      This beneficiary has not been assigned a bursary code yet. The code will be generated automatically when the beneficiary record is saved.
                    </p>
                    {beneficiary.beneficiary_bursary_info.error && (
                      <div className="mt-2">
                        <small className="text-danger">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {beneficiary.beneficiary_bursary_info.error}
                        </small>
                      </div>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Organization Hierarchy */}
          {beneficiary.funder_info && beneficiary.bmo_info && (
            <Card>
              <CardHeader className="bg-success text-white">
                <h5 className="mb-0">
                  <i className="fas fa-sitemap me-2"></i>
                  Organization Hierarchy
                </h5>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={4}>
                    <h6 className="text-muted mb-2">
                      <i className="fas fa-building me-2"></i>
                      BMO (Bursary Management Organization)
                    </h6>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Name:</strong></td>
                          <td>{beneficiary.bmo_info.name}</td>
                        </tr>
                        <tr>
                          <td><strong>Code:</strong></td>
                          <td>
                            <span className="font-monospace">
                              {beneficiary.bmo_info.code || '-'}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={4}>
                    <h6 className="text-muted mb-2">
                      <i className="fas fa-handshake me-2"></i>
                      Funder
                    </h6>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Name:</strong></td>
                          <td>{beneficiary.funder_info.name}</td>
                        </tr>
                        <tr>
                          <td><strong>Code:</strong></td>
                          <td>
                            <span className="font-monospace">
                              {beneficiary.funder_info.funder_code || '-'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Country:</strong></td>
                          <td>{beneficiary.funder_info.country || '-'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={4}>
                    <h6 className="text-muted mb-2">
                      <i className="fas fa-clock me-2"></i>
                      System Information
                    </h6>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Created:</strong></td>
                          <td>{formatDate(beneficiary.created_at)}</td>
                        </tr>
                        <tr>
                          <td><strong>Last Updated:</strong></td>
                          <td>{formatDate(beneficiary.updated_at)}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default BeneficiaryDetail;